import React, { useState } from 'react';
import { useTranslations } from 'next-intl';
import { Frames } from '@monofront/frames-react';
import { toast } from 'react-toastify';
import { useLazyQuery, useMutation } from '@apollo/client';
import { CHECKOUTCOM_CARD_FORM_TRANSITIONS } from '@monofront/business/stateMachines/checkout/checkoutcom/transitions';
import {
  CREATE_PAYMENT_WITH_3DS,
  GET_PAYMENT,
} from '@monofront/business/queries';
import {
  ICreatePaymentWith3DSResult,
  IGetPaymentResult,
  PaymentMethodSource,
  PaymentMethodType,
} from '@monofront/types';
import {
  useCheckoutComMachine,
  CheckoutComMachineProvider,
} from '@monofront/business/contexts';
import logger from '@utils/logger';
import CheckoutComModal3DS from '@features/PaymentMethods/CheckoutComModal3DS';
import CheckoutComCardFormAbTest from '../CheckoutComCardForm/CheckoutComCardFormAbTest';
import CheckoutComFlowCardFields from '../CheckoutComFlowCardFields';
import { sendMessageToMobile } from '@utils/mobile';

interface ISubscriptionFormProps {
  handleSubmitCard: (
    paymentMethodId: string,
    paymentMethodeType: PaymentMethodType,
    paymentMethodSource: PaymentMethodSource,
    financialPaymentId: string,
  ) => Promise<void> | void;
  onComplete?: (isValid: boolean) => void;
  handleFinish?: () => void;
  isFlowEnabled?: boolean;
  children?: React.ReactNode;
}

const SubscriptionCardForm: React.FC<ISubscriptionFormProps> = ({
  children,
  handleSubmitCard,
  onComplete,
  handleFinish,
  isFlowEnabled = false,
}) => {
  const { state, send } = useCheckoutComMachine();
  const [createPaymentWith3DS] = useMutation<ICreatePaymentWith3DSResult>(
    CREATE_PAYMENT_WITH_3DS,
  );
  const [getPayment] = useLazyQuery<IGetPaymentResult>(GET_PAYMENT);
  const [isLoading, setIsLoading] = useState(false);
  const tErrors = useTranslations('Errors');

  const handleFinishPayment = async () => {
    const { data, error } = await getPayment({
      variables: {
        paymentId: state.context.paymentId,
      },
    });

    if (error || !data) {
      logger.error('Failed to get payment', { error });
      toast(tErrors('paymentGenericError'), {
        progressClassName: 'toast-error',
      });
      setIsLoading(false);
      send({ type: CHECKOUTCOM_CARD_FORM_TRANSITIONS.RESET });

      return;
    }

    handleSubmitCard(
      data.getPayment.paymentMethodId,
      PaymentMethodType.CB,
      PaymentMethodSource.CHECKOUT,
      state.context.paymentId!,
    );
    handleFinish?.();
  };

  const reportPaymentFailure = (error: unknown) => {
    logger.error(
      'An error occurred during validation of the card form for subscription.',
      { error },
    );
    toast(tErrors('paymentGenericError'), {
      progressClassName: 'toast-error',
    });
    send({ type: CHECKOUTCOM_CARD_FORM_TRANSITIONS.RESET });
  };

  const submitCardPayment = async (token: string) => {
    try {
      setIsLoading(true);

      send({ type: CHECKOUTCOM_CARD_FORM_TRANSITIONS.SUBMIT_CARD });
      const asyncPayment = await createPaymentWith3DS({
        variables: {
          token,
          paymentMethodSource: PaymentMethodSource.CHECKOUT,
        },
      });

      if (!asyncPayment.data) {
        throw new Error('Failed to generate payment with 3DS');
      }

      send({
        type: CHECKOUTCOM_CARD_FORM_TRANSITIONS.CREATE_PAYMENT_WITH_3DS,
        url3DSecure: asyncPayment.data.createPaymentWith3DS.redirectLink,
        paymentId: asyncPayment.data.createPaymentWith3DS.id,
      });
    } catch (error) {
      reportPaymentFailure(error);
    } finally {
      setIsLoading(false);
    }
  };

  const submitFramesCard = async () => {
    try {
      setIsLoading(true);
      const card = await Frames.submitCard();

      await submitCardPayment(card.token);
    } catch (error) {
      reportPaymentFailure(error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isFlowEnabled) {
    return (
      <CheckoutComFlowCardFields
        onSubmit={submitCardPayment}
        isLoading={isLoading}
        onComplete={onComplete}
      >
        {children}
        <CheckoutComModal3DS on3dsSuccess={handleFinishPayment} />
      </CheckoutComFlowCardFields>
    );
  }

  return (
    <CheckoutComCardFormAbTest
      onSubmit={submitFramesCard}
      isLoading={isLoading}
      onComplete={onComplete}
    >
      {children}
      <CheckoutComModal3DS on3dsSuccess={handleFinishPayment} />
    </CheckoutComCardFormAbTest>
  );
};

const SubscriptionCardFormWrapper: React.FC<ISubscriptionFormProps> = ({
  children,
  ...props
}) => {
  return (
    <CheckoutComMachineProvider sendMessageToMobile={sendMessageToMobile}>
      <SubscriptionCardForm {...props}>{children}</SubscriptionCardForm>
    </CheckoutComMachineProvider>
  );
};

export default SubscriptionCardFormWrapper;
